import React, { useCallback, useEffect, useRef, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useWorkspaces } from "@/contexts/WorkspaceContext";
import { Menu } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import WorkspaceRail from "@/components/WorkspaceRail";
import Sidebar from "@/components/Sidebar";
import ChatPane from "@/components/ChatPane";
import ChatErrorBoundary from "@/components/ChatErrorBoundary";
import SettingsModal from "@/components/SettingsModal";
import TrialBanner from "@/components/TrialBanner";
import SuspensionBanner from "@/components/SuspensionBanner";
import { toast } from "sonner";

const CHATS_POLL_MS = 5000;

export default function WorkspacePage() {
  const { slug, chatId } = useParams();
  const { user } = useAuth();
  const { workspaces, loading: wsLoading } = useWorkspaces();
  const navigate = useNavigate();

  const [chats, setChats] = useState([]);
  const [loadingChats, setLoadingChats] = useState(true);
  const [otherUsers, setOtherUsers] = useState([]);
  const [settingsOpen, setSettingsOpen] = useState(false);
  // Phase 10 — mobile drawer state. On md+ this is irrelevant (CSS keeps
  // both sidebars visible); on mobile this toggles a slide-in overlay.
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const pollRef = useRef(null);

  const workspace =
    workspaces.find(
      (w) =>
        w.slug === slug || (w.previous_slugs || []).includes(slug),
    ) || null;
  const workspaceId = workspace?.id || null;
  const activeChatId = chatId || null;

  // Phase Rebrand — if user landed via legacy slug (e.g. /w/sonrie), rewrite
  // the URL to the canonical slug (/w/onebrain) so bookmarks update silently.
  const isLegacySlug =
    workspace !== null && workspace.slug !== slug;
  useEffect(() => {
    if (isLegacySlug && workspace) {
      const target = chatId
        ? `/w/${workspace.slug}/c/${chatId}`
        : `/w/${workspace.slug}`;
      navigate(target, { replace: true });
    }
  }, [isLegacySlug, workspace, chatId, navigate]);

  const fetchChats = useCallback(
    async ({ silent } = { silent: false }) => {
      if (!workspaceId) return null;
      try {
        const { data } = await api.get(`/workspaces/${workspaceId}/chats`);
        setChats(data || []);
        return data || [];
      } catch (e) {
        if (!silent) toast.error(formatApiError(e));
        return null;
      } finally {
        if (!silent) setLoadingChats(false);
      }
    },
    [workspaceId]
  );

  // Reset chat list whenever the workspace changes, then load.
  // Synchronous setState here gives immediate visual feedback (clear stale
  // chats + show skeleton) before the async fetchChats() resolves.
  useEffect(() => {
    if (workspaceId && user?.id) {
      setChats([]);
      setLoadingChats(true);
      fetchChats();
    }
  }, [workspaceId, user?.id, fetchChats]);

  // Workspace-scoped @mention picker users.
  // Reset-on-workspace-change is intentional so we don't leak the previous
  // workspace's member list into the picker while the next fetch is in flight.
  useEffect(() => {
    if (!workspaceId) {
      setOtherUsers([]);
      return;
    }
    api
      .get(`/workspaces/${workspaceId}/users`)
      .then(({ data }) => setOtherUsers(Array.isArray(data) ? data : []))
      .catch(() => setOtherUsers([]));
  }, [workspaceId]);

  // Poll the chat list every 5s
  useEffect(() => {
    if (!workspaceId) return undefined;
    pollRef.current = setInterval(() => {
      fetchChats({ silent: true });
    }, CHATS_POLL_MS);
    return () => clearInterval(pollRef.current);
  }, [workspaceId, fetchChats]);

  if (user === null) {
    return (
      <div className="h-screen w-full flex items-center justify-center bg-[#FAF9F7]">
        <p className="font-sans text-sm text-[#7A7571]">Loading…</p>
      </div>
    );
  }
  if (!user || !user.id) return <Navigate to="/login" replace />;

  if (wsLoading) {
    return (
      <div className="h-screen w-full flex items-center justify-center bg-[#FAF9F7]">
        <p className="font-sans text-sm text-[#7A7571]">
          Loading workspaces…
        </p>
      </div>
    );
  }

  // Slug doesn't match a workspace this user is a member of — bounce.
  if (!workspace) {
    if (workspaces.length > 0) {
      return <Navigate to={`/w/${workspaces[0].slug}`} replace />;
    }
    return <Navigate to="/all-chats" replace />;
  }

  const activeChat = chats.find((c) => c.id === activeChatId) || null;

  const handleSelectChat = (id) => {
    setMobileNavOpen(false);
    navigate(`/w/${workspace.slug}/c/${id}`);
  };

  const handleNewChat = async () => {
    try {
      const { data } = await api.post(`/workspaces/${workspaceId}/chats`, {
        title: "New chat",
      });
      setChats((prev) => [data, ...prev.filter((c) => c.id !== data.id)]);
      navigate(`/w/${workspace.slug}/c/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const handleRenameChat = async (id, title) => {
    const t = (title || "").trim();
    if (!t) return;
    try {
      const { data } = await api.patch(
        `/workspaces/${workspaceId}/chats/${id}`,
        { title: t }
      );
      setChats((prev) => prev.map((c) => (c.id === id ? data : c)));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const handleDeleteChat = async (id) => {
    try {
      await api.delete(`/workspaces/${workspaceId}/chats/${id}`);
      setChats((prev) => prev.filter((c) => c.id !== id));
      if (activeChatId === id) navigate(`/w/${workspace.slug}`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div
      className="flex h-screen w-full bg-[#FAF9F7] overflow-hidden"
      data-testid="workspace"
      data-workspace-slug={workspace.slug}
      data-workspace-id={workspace.id}
    >
      {/* Desktop: rail + sidebar visible. Mobile: hidden until drawer opens */}
      <div className="hidden md:flex">
        <WorkspaceRail activeWorkspaceId={workspaceId} />
        <Sidebar
          workspace={workspace}
          chats={chats}
          loading={loadingChats}
          activeChatId={activeChatId}
          onSelectChat={handleSelectChat}
          onNewChat={handleNewChat}
          onRenameChat={handleRenameChat}
          onDeleteChat={handleDeleteChat}
          currentUser={user}
          onOpenSettings={() => setSettingsOpen(true)}
        />
      </div>

      {/* Mobile drawer overlay */}
      {mobileNavOpen ? (
        <div
          className="md:hidden fixed inset-0 z-40 bg-black/40 backdrop-blur-sm"
          onClick={() => setMobileNavOpen(false)}
          data-testid="mobile-nav-backdrop"
        />
      ) : null}
      <div
        className={`md:hidden fixed inset-y-0 left-0 z-50 flex transform transition-transform duration-200 ${
          mobileNavOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        data-testid="mobile-nav-drawer"
      >
        <WorkspaceRail activeWorkspaceId={workspaceId} />
        <Sidebar
          workspace={workspace}
          chats={chats}
          loading={loadingChats}
          activeChatId={activeChatId}
          onSelectChat={handleSelectChat}
          onNewChat={() => {
            setMobileNavOpen(false);
            handleNewChat();
          }}
          onRenameChat={handleRenameChat}
          onDeleteChat={handleDeleteChat}
          currentUser={user}
          onOpenSettings={() => {
            setMobileNavOpen(false);
            setSettingsOpen(true);
          }}
        />
      </div>

      <div className="flex-1 min-w-0 flex flex-col overflow-hidden">
        {/* Phase 10 — mobile-only top bar with hamburger + workspace name */}
        <div
          className="md:hidden flex items-center gap-3 h-12 px-3 border-b border-[#ECE7DC] bg-[#FAF9F7]"
          data-testid="mobile-top-bar"
        >
          <button
            type="button"
            onClick={() => setMobileNavOpen(true)}
            className="p-2 -ml-2 text-[#2A2D34]"
            aria-label="Open chats menu"
            data-testid="mobile-nav-toggle"
          >
            <Menu size={20} />
          </button>
          <p className="font-serif text-base text-[#2A2D34] truncate flex-1">
            {workspace?.name || "Workspace"}
            <span className="text-[#1E7BFF]">.</span>
          </p>
        </div>
        <SuspensionBanner
          workspace={workspace}
          onOpenBilling={() => setSettingsOpen(true)}
        />
        <TrialBanner
          workspace={workspace}
          onOpenSettings={() => setSettingsOpen(true)}
        />
        <ChatErrorBoundary>
          <ChatPane
            workspaceId={workspaceId}
            workspace={workspace}
            chat={activeChat}
            currentUser={user}
            otherUsers={otherUsers}
            onRenameChat={handleRenameChat}
            onChatActivity={() => fetchChats({ silent: true })}
            onNewChat={handleNewChat}
            onOpenSettings={() => setSettingsOpen(true)}
          />
        </ChatErrorBoundary>
      </div>
      <SettingsModal
        workspace={workspace}
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
      />
    </div>
  );
}
